import { helpers, models } from '@kurtosys/ksys-app-template';
import { computed, observable } from 'mobx';
import stringify from 'json-stable-stringify';
import { BaseStore } from './BaseStore';
import { Stores } from '../models/Stores';
import { ListenForOptions } from '../models/configuration/ListenForOptions';

type EventBusStoreCallback = (customEvent: CustomEvent<any>) => Promise<void>;
type EventBusStoreCallbacks = Partial<
    Record<models.eventBus.EventType, EventBusStoreCallback>
>;
interface ListenOptions {
    appId: string;
    callback: EventBusStoreCallback;
    updateRequiredTracking?: boolean;
}

export class EventBusStore extends BaseStore {
    callbacks: EventBusStoreCallbacks | undefined;
    eventCache: Map<string, string> = new Map();
    helper: helpers.EventBusHelper | undefined;
    @observable.ref requiredCallbackTracking: Record<string, boolean> = {};

    constructor(stores: Stores) {
        super(stores);
    }

    @computed
    get listenFor(): Array<ListenForOptions> {
        if (this.stores.data) {
            return this.stores.data.config.listenFor;
        }

        return [];
    }

    @computed
    get applicationId(): string | undefined {
        if (this.stores.data) {
            return this.stores.data.config.applicationId;
        }

        return;
    }

    @computed
    public get requiredCallbacksCompleted() {
        if (Object.keys(this.requiredCallbackTracking).length === 0) {
            return true;
        }

        let completed = true;
        for (const name in this.requiredCallbackTracking) {
            if (!this.requiredCallbackTracking[name]) {
                completed = false;
                break;
            }
        }
        return completed;
    }

    setupHelper() {
        this.helper = new helpers.EventBusHelper(this.applicationId);
    }

    async initialize(callbacks: EventBusStoreCallbacks | undefined) {
        this.callbacks = callbacks;
    }

    isListeningFor(appId: string, eventName: string): boolean {
        const app = this.listenFor.find(appConfig => {
            return appConfig.appId === appId;
        });
        const eventConfig =
            app &&
            // tslint:disable-next-line:no-shadowed-variable
            app.eventTypes.find(eventConfig => {
                return eventConfig.name === eventName;
            });

        return Boolean(eventConfig);
    }

    /**
     * Start listening for events that are required for initial load
     */
    public async register() {
        const listenForEvents = this.listenFor;
        if (!this.helper) {
            this.setupHelper();
        }

        if (listenForEvents.length > 0 && this.helper !== undefined) {
            listenForEvents.forEach(listenForEvent => {
                const appId = listenForEvent.appId;
                const appEventTypes = listenForEvent.eventTypes;

                if (appEventTypes.length > 0) {
                    appEventTypes.forEach(appEvent => {
                        const name = appEvent.name as models.eventBus.EventType;
                        const requiredForInitialLoad =
                            appEvent.requiredForInitialLoad;
                        // Wait for the eventBus Store to retrieve all events that are required before continuing with initialzing of the app
                        const targetCallback =
                            this.callbacks && this.callbacks[name];
                        if (targetCallback) {
                            const storageValue = this.getStorageValue(
                                appId,
                                name
                            );
                            // Checks if the event is required and if we already have the event, then we don't need to set up additional tracking for it
                            if (!storageValue && requiredForInitialLoad) {
                                this.requiredCallbackTracking = {
                                    ...this.requiredCallbackTracking,
                                    [name]: false,
                                };
                                this.listen(name, {
                                    appId,
                                    callback: targetCallback,
                                    updateRequiredTracking: true,
                                });
                            } else {
                                this.listen(name, {
                                    appId,
                                    callback: targetCallback,
                                });
                            }

                            if (
                                storageValue &&
                                this.validateEventCache(
                                    storageValue.detail,
                                    appId,
                                    name
                                )
                            ) {
                                targetCallback(storageValue);
                            }
                        }
                    });
                }
            });
        }
    }

    validateEventCache(
        eventValue: Record<string, any>,
        appId: string,
        eventType: string
    ) {
        const key = eventType;
        const evtValue = stringify(eventValue);
        if (
            !this.eventCache.has(key) ||
            this.eventCache.get(key) !== evtValue
        ) {
            this.eventCache.set(key, evtValue);
            return true;
        }
        return false;
    }

    getStorageValue(appId: string, eventType: models.eventBus.EventType) {
        const eventDetails = sessionStorage.getItem('eventDetails');
        if (eventDetails) {
            try {
                const parsedDetails = JSON.parse(eventDetails);
                if (
                    parsedDetails &&
                    parsedDetails[appId] &&
                    parsedDetails[appId][eventType]
                ) {
                    return parsedDetails[appId][eventType];
                }
            } catch (e) {
                console.warn(`Failed to parse event storage for ${eventType}`);
            }
        }
        return;
    }

    trigger(
        type: models.eventBus.EventType,
        detail: { detail: Record<string, any> }
    ) {
        if (this.helper) {
            this.helper.trigger(type, detail);
        } else {
            console.warn(
                `Unable to trigger event, as no the helper not yet defined. The helper is defined as part of the event bus store register.`
            );
        }
    }

    private listen(type: models.eventBus.EventType, options: ListenOptions) {
        if (this.helper) {
            const {
                appId,
                callback,
                updateRequiredTracking: updatedRequiredTracking = false,
            } = options;
            this.helper.register(type, (evt: any) => {
                if (updatedRequiredTracking) {
                    this.requiredCallbackTracking = {
                        ...this.requiredCallbackTracking,
                        [type]: true,
                    };
                }
                if (this.validateEventCache(evt.detail, appId, type)) {
                    callback(evt);
                }
            });
        } else {
            console.warn(
                `Unable to register event type "${type}", as the Event Bus Helper is not yet defined. It is defined as part of the event bus store register method.`
            );
        }
    }
}



// WEBPACK FOOTER //
// ./src/stores/EventBusStore.ts